import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Typography } from '@mui/material';
import LegalPage, { LegalSection } from '../components/LegalPage';
import { LEGAL } from '../legal';
import { BRAND } from '../theme';

const email = <Link href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</Link>;
const P: React.FC<{ children: React.ReactNode }> = ({ children }) => <Typography component="p">{children}</Typography>;
const privacyLink = (
  <Link component={RouterLink} to="/privacy">
    Privacy Policy
  </Link>
);

const sections: LegalSection[] = [
  {
    id: 'agreement',
    title: 'Agreement',
    body: (
      <>
        <P>
          These Terms are an agreement between you and {LEGAL.operatorName} ("we", "us") covering your use of{' '}
          {BRAND.name} (the "Service"). By creating an account or using the Service, you accept these Terms and our{' '}
          {privacyLink}. If you don't agree, don't use the Service.
        </P>
        <P>
          You must be at least {LEGAL.minimumAge} years old. If you are under {LEGAL.adultAge}, you may use the
          Service only with the permission of a parent or legal guardian, who must read and accept these Terms on
          your behalf and is responsible for your use of the Service. If you use the Service for an organisation, you
          confirm that you are authorised to accept these Terms for it.
        </P>
      </>
    ),
  },
  {
    id: 'service',
    title: 'The Service',
    body: (
      <>
        <P>
          {BRAND.name} lets you manage tasks and categories, log time, see analytics about your time, and use an AI
          assistant that can read and change that data when you ask it to. It contains no adult content.
        </P>
        <P>
          The Service is currently free. Some features, such as the AI assistant, may become paid in the future,
          and free use may be subject to limits. Before any feature becomes paid, we will tell you the price and
          terms in advance; you will not be charged unless you choose to pay, and anyone under {LEGAL.adultAge} needs
          a parent's or guardian's permission to do so. Paid features will be covered by additional terms shown
          when you buy them.
        </P>
      </>
    ),
  },
  {
    id: 'accounts',
    title: 'Your account',
    body: (
      <ul>
        <li>Give accurate information and keep it up to date.</li>
        <li>
          Keep your password secret (or your Google or Facebook account secure, if you sign in with one). You are
          responsible for activity under your account.
        </li>
        <li>Tell us promptly at {email} if you suspect unauthorised use of your account.</li>
      </ul>
    ),
  },
  {
    id: 'your-content',
    title: 'Your content',
    body: (
      <>
        <P>
          You own what you put into {BRAND.name}: your tasks, categories, time logs and messages ("your content").
          You give us a limited licence to store, process and display your content, and to send it to our service
          providers (including the AI provider described in the {privacyLink}), only as needed to run the Service for
          you. This licence ends when your content is deleted.
        </P>
        <P>
          You are responsible for your content and confirm that you have the right to use it. Please don't put
          sensitive personal data, or other people's personal data without their permission, into the Service.
        </P>
      </>
    ),
  },
  {
    id: 'ai-assistant',
    title: 'The AI assistant',
    body: (
      <ul>
        <li>
          The assistant is powered by an AI model. Its answers can be wrong, incomplete or out of date. Check
          anything important before you rely on it.
        </li>
        <li>
          When you ask, the assistant creates, changes or deletes your tasks, categories and time logs. Review the
          changes it makes; you are responsible for instructions you give it.
        </li>
        <li>It does not give professional (legal, financial, medical or other) advice.</li>
        <li>
          It is meant for managing your tasks and time. We limit message length and how often you can send messages,
          and may decline requests outside that purpose.
        </li>
      </ul>
    ),
  },
  {
    id: 'acceptable-use',
    title: 'Acceptable use',
    body: (
      <>
        <P>You agree not to:</P>
        <ul>
          <li>break the law or infringe anyone's rights using the Service;</li>
          <li>try to access other users' accounts or data, or probe, scan or test the Service's security without our written permission;</li>
          <li>get around usage limits or security measures, including by trying to manipulate the AI assistant into ignoring its rules;</li>
          <li>use the AI assistant to create unlawful, harmful, abusive or misleading content;</li>
          <li>
            enter or try to generate sexually explicit, violent or otherwise age-inappropriate content: the Service
            is meant to be suitable for young users;
          </li>
          <li>overload or disrupt the Service, or access it with bots or scrapers;</li>
          <li>upload malware, or copy, resell or reverse-engineer the Service except as the law allows.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'availability',
    title: 'Availability and changes',
    body: (
      <P>
        We work to keep the Service running, but we don't promise it will always be available, uninterrupted or free
        of errors. We may change, add or remove features, and may suspend or stop the Service. If we plan to shut it
        down, we will give you reasonable notice so you can copy your data. Keep your own copies of anything
        important.
      </P>
    ),
  },
  {
    id: 'termination',
    title: 'Ending your use',
    body: (
      <P>
        You can stop using the Service at any time and ask us to delete your account (see the {privacyLink}). We may
        suspend or close your account if you seriously or repeatedly break these Terms, if the law requires it, or to
        protect the Service or other users. Where reasonable, we will tell you why first. Sections 4 and 9 to 13
        continue to apply after your account ends.
      </P>
    ),
  },
  {
    id: 'disclaimer',
    title: 'Disclaimer',
    body: (
      <P>
        To the extent the law allows, the Service is provided "as is" and "as available", without warranties of any
        kind, whether express or implied, including warranties of merchantability, fitness for a particular purpose,
        accuracy and non-infringement.
      </P>
    ),
  },
  {
    id: 'liability',
    title: 'Limitation of liability',
    body: (
      <>
        <P>
          To the extent the law allows, we are not liable for indirect, incidental, special or consequential losses,
          or for lost profits, revenue, data or goodwill, arising from your use of the Service, including from errors
          in AI output. Our total liability for all claims relating to the Service is limited to the greater of the
          amount you paid us for the Service in the 12 months before the claim and INR 1,000.
        </P>
        <P>
          Nothing in these Terms limits liability that cannot be limited by law, such as for fraud, or your rights
          under consumer protection laws that apply to you.
        </P>
      </>
    ),
  },
  {
    id: 'indemnity',
    title: 'Indemnity',
    body: (
      <P>
        To the extent the law allows, you will compensate us for reasonable losses and costs arising from third-party
        claims caused by your content or by your breach of these Terms.
      </P>
    ),
  },
  {
    id: 'third-parties',
    title: 'Third-party services',
    body: (
      <P>
        The Service relies on third-party services, including Microsoft Azure (hosting and AI) and, if you use them,
        Google and Facebook sign-in. Your use of those services is also governed by their own terms, and we are not
        responsible for them.
      </P>
    ),
  },
  {
    id: 'governing-law',
    title: 'Governing law and disputes',
    body: (
      <P>
        These Terms are governed by the laws of India. Please contact us first at {email} so we can try to resolve
        any dispute informally. If we can't, the courts of {LEGAL.courtsCity} have exclusive jurisdiction, without
        affecting any mandatory right you have as a consumer to bring proceedings where you live.
      </P>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to these Terms',
    body: (
      <P>
        We may update these Terms. We'll change the date at the top and, for significant changes, tell you in the app
        or by email before they take effect. If you keep using the Service after that, the new Terms apply. If you
        don't agree, stop using the Service and you can ask us to delete your account.
      </P>
    ),
  },
  {
    id: 'general',
    title: 'General',
    body: (
      <P>
        These Terms and the {privacyLink} are the whole agreement between you and us about the Service. If any part
        is found unenforceable, the rest still applies. If we don't enforce a right straight away, we don't give it
        up. You may not transfer your rights under these Terms. We may transfer ours to a successor that continues
        the Service, and will tell you if we do.
      </P>
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    body: (
      <P>
        {LEGAL.operatorName}, {LEGAL.postalAddress}. Email: {email}.
      </P>
    ),
  },
];

const TermsPage: React.FC = () => (
  <LegalPage
    title="Terms of Service"
    intro={<P>Please read these Terms carefully. They explain your rights and responsibilities when you use {BRAND.name}.</P>}
    sections={sections}
  />
);

export default TermsPage;
